import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import ProductCard from "../components/ProductCard";
import { fetchProducts } from "../lib/api";

/**
 * CategoryPage — one universe (spices, textiles, deco, oils).
 * Optionally shows sub-category chips when the universe defines them.
 */
const CategoryPage = ({ category, headlineFr, headlineEn, sublabelFr, sublabelEn }) => {
  const { t, lang } = useLang();
  const [params, setParams] = useSearchParams();
  const sub = params.get("sub") || "all";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const subcats = t.subcats?.[category];

  useEffect(() => {
    setLoading(true);
    const wantedSub = sub === "all" ? undefined : sub;
    fetchProducts(category, wantedSub)
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [category, sub]);

  const subKeys = useMemo(() => (subcats ? Object.keys(subcats) : []), [subcats]);

  const setSub = (k) => {
    if (k === "all") setParams({});
    else setParams({ sub: k });
  };

  return (
    <div className="pt-28 md:pt-36 pb-24" data-testid={`page-cat-${category}`}>
      <div className="px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14 md:mb-20">
          <div className="lg:col-span-7">
            <p className="overline mb-4">{t.categories[category]}</p>
            <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl font-light text-[#1E2B3C] leading-[1.02]">
              {lang === "fr" ? headlineFr : headlineEn}
            </h1>
          </div>
          <p className="lg:col-span-5 text-[#5C5C5C] leading-relaxed max-w-md lg:justify-self-end">
            {lang === "fr" ? sublabelFr : sublabelEn}
          </p>
        </div>

        {/* Sub-section chips */}
        {subcats && (
          <div
            className="border-y border-[#E5E1D8] py-4 md:py-5 flex flex-wrap gap-1 md:gap-2 mb-12 md:mb-16"
            data-testid="sub-filters"
          >
            {subKeys.map((k) => {
              const active = sub === k;
              return (
                <button
                  key={k}
                  onClick={() => setSub(k)}
                  className={`inline-flex items-center gap-2 px-4 md:px-5 py-2 text-xs tracking-[0.18em] uppercase font-medium transition-colors ${
                    active
                      ? "bg-[#1E2B3C] text-white"
                      : "text-[#1A1A1A] hover:bg-[#F2EFE9]"
                  }`}
                  data-testid={`subfilter-${k}`}
                >
                  {subcats[k]}
                </button>
              );
            })}
          </div>
        )}

        {loading ? (
          <div className="py-32 text-center text-[#5C5C5C]" data-testid="cat-loading">…</div>
        ) : products.length === 0 ? (
          <div className="py-32 text-center text-[#5C5C5C]" data-testid="cat-empty">
            {lang === "fr" ? "Aucune pièce dans cet univers." : "No pieces in this universe."}
          </div>
        ) : (
          <div
            className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-5 gap-y-12 md:gap-x-8 md:gap-y-20"
            data-testid="cat-grid"
          >
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryPage;
